import { useCallback, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import useVoiceActions from './useVoiceActions';
import { useCallUiEvents } from '../../calls/WhatsappCallsRuntimeContext';
import VoiceCallsApiClient from '../infrastructure/api/VoiceCallsApiClient';
import {
  voiceRemoveIncomingCall,
  voiceSetOutboundDialing,
  voiceClearOutboundDialing,
  voiceSetCurrentCallControlId,
  voiceSetError,
  voiceSetMuted,
  voiceReleaseCallSession,
  voiceSetLoading
} from '../../../../actions/voice/voiceActions';
import {
  connectAgentWebRtcClient,
  markAgentCallAnswerPending,
  muteCurrentAgentCall,
  unmuteCurrentAgentCall,
  isCurrentAgentCallMuted,
  releaseAgentLocalAudioCapture
} from '../infrastructure/webrtc/voiceAgentWebRtcAdapter';
import {
  completeVoiceCallHangup,
  resolveEndedReasonForVoiceCall,
  claimVoiceTabAnswer,
  getVoiceTabClaimedCallControlId
} from './voiceHangupCoordinator';
import executeAnswerCallUseCase from './answerCallUseCase';
import { VOICE_ANSWER_ERROR } from '../domain/voiceAnswerErrors';
import {
  VOICE_ENDED_REASON_HANGUP,
  VOICE_ENDED_REASON_AGENT_DECLINED,
  VOICE_ENDED_REASON_AGENT_CANCELLED
} from '../domain/voiceHangupEndedReason';

/**
 * Fonema-compatible call actions (Voice Platform). Only mount under voice runtime tree.
 */
export default function useVoiceCallActionsBridge({
  disableButtons,
  callInfoData,
  enabled = true
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation('Voice');
  const callUiEvents = useCallUiEvents();
  const { answerCall: answerCallApi } = useVoiceActions({ enabled });
  const callInfoDataRef = useRef(callInfoData);
  callInfoDataRef.current = callInfoData;
  const answerInFlightRef = useRef(false);
  const answerAbandonedRef = useRef(false);
  const hangupState = useSelector(
    (state) => ({
      activeCallControlId: state.voiceReducer?.activeCall?.callControlId ?? null,
      outboundDialingCallControlId: state.voiceReducer?.outboundDialingCall?.callControlId ?? null
    }),
    shallowEqual
  );
  const hangupStateRef = useRef(hangupState);
  hangupStateRef.current = hangupState;

  const resetAfterHangupHttp = useCallback((callId) => {
    releaseAgentLocalAudioCapture();
    dispatch(voiceSetMuted(false));
    if (callId) {
      dispatch(voiceReleaseCallSession(callId));
    }
  }, [dispatch]);

  const hangupTrackedCall = useCallback(async (callId, endedReason) => {
    if (!callId) return;
    dispatch(voiceSetLoading(true));
    dispatch(voiceSetError(null));
    try {
      await completeVoiceCallHangup({
        callControlId: callId,
        endedReason,
        onAfterHttpOk: () => resetAfterHangupHttp(callId)
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to hang up call';
      dispatch(voiceSetError(message));
      throw error;
    } finally {
      dispatch(voiceSetLoading(false));
    }
  }, [dispatch, resetAfterHangupHttp]);

  const noop = useCallback(async () => {}, []);
  const noopBool = useCallback(() => false, []);

  const answerCall = useCallback(async () => {
    if (!enabled) return;
    const callId = callInfoDataRef.current?.callId;
    if (!callId) return;

    const result = await executeAnswerCallUseCase({
      callId,
      hangupState: hangupStateRef.current,
      dispatch,
      answerCallApi,
      answerAbandonedRef,
      answerInFlightRef,
      disableButtons,
      onStopRingtone: () => callUiEvents?.stopRingtone?.()
    });

    if (result.ok || result.ignored) return;
    if (result.code === VOICE_ANSWER_ERROR.ABORTED) return;

    const errorKey = result.code && result.code !== VOICE_ANSWER_ERROR.REQUEST_TIMEOUT
      ? `errors.${result.code}`
      : 'errors.generic';
    dispatch(voiceSetError(t(errorKey, { defaultValue: t('errors.generic') })));
  }, [enabled, disableButtons, answerCallApi, dispatch, t, callUiEvents]);

  const declineCall = useCallback(async () => {
    if (!enabled) return;
    const callId = callInfoDataRef.current?.callId;
    if (!callId) return;
    answerAbandonedRef.current = true;
    disableButtons();
    const endedReason = resolveEndedReasonForVoiceCall(hangupStateRef.current, callId)
      === VOICE_ENDED_REASON_HANGUP
      ? VOICE_ENDED_REASON_HANGUP
      : VOICE_ENDED_REASON_AGENT_DECLINED;
    await hangupTrackedCall(callId, endedReason);
    dispatch(voiceRemoveIncomingCall(callId));
  }, [enabled, disableButtons, hangupTrackedCall, dispatch]);

  const endCall = useCallback(async () => {
    if (!enabled) return;
    const callId = callInfoDataRef.current?.callId;
    if (!callId) return;
    answerAbandonedRef.current = true;
    disableButtons();
    const endedReason = resolveEndedReasonForVoiceCall(hangupStateRef.current, callId);
    await hangupTrackedCall(callId, endedReason);
  }, [enabled, disableButtons, hangupTrackedCall]);

  const toggleMic = useCallback(() => {
    if (!enabled) return false;

    const wasMuted = isCurrentAgentCallMuted();
    const didToggle = wasMuted
      ? unmuteCurrentAgentCall()
      : muteCurrentAgentCall();

    if (!didToggle) {
      return wasMuted;
    }

    const isMuted = isCurrentAgentCallMuted();
    dispatch(voiceSetMuted(isMuted));
    return isMuted;
  }, [enabled, dispatch]);

  const call = useCallback(async ({ to, from, appId } = {}) => {
    if (!enabled) return false;
    if (!to) return false;
    const fromNumber = from || callInfoDataRef.current?.registerNumber;
    if (!fromNumber) {
      dispatch(voiceSetError('Selecciona la línea desde la que vas a llamar'));
      return false;
    }
    if (hangupStateRef.current.activeCallControlId || getVoiceTabClaimedCallControlId()) {
      dispatch(voiceSetError('Ya hay una llamada en curso en esta pestaña'));
      return false;
    }
    disableButtons();
    try {
      await connectAgentWebRtcClient();
      const resolvedAppId = appId || callInfoDataRef.current?.appId || undefined;
      const response = await VoiceCallsApiClient.createCall({
        to,
        from: fromNumber,
        customerId: callInfoDataRef.current?.customer?.id,
        ...(resolvedAppId ? { appId: resolvedAppId } : {})
      });
      const { callControlId } = response;
      if (!claimVoiceTabAnswer(callControlId)) {
        dispatch(voiceSetError('Ya hay una llamada en curso en esta pestaña'));
        return false;
      }
      callInfoDataRef.current.callId = callControlId;
      dispatch(voiceSetCurrentCallControlId(callControlId));
      markAgentCallAnswerPending(callControlId);
      dispatch(voiceSetOutboundDialing({
        callControlId,
        callSessionId: response.callSessionId ?? '',
        callLegId: response.callLegId ?? '',
        from: response.from ?? fromNumber ?? callInfoDataRef.current?.registerNumber ?? '',
        to: response.to ?? to,
        direction: 'outgoing',
        timestamp: response.timestamp ?? new Date().toISOString(),
        channel: response.channel ?? 'unknown'
      }));

      return true;
    } catch (error) {
      dispatch(voiceClearOutboundDialing());
      const message = error instanceof Error ? error.message : 'Failed to create outbound call';
      dispatch(voiceSetError(message));
      // eslint-disable-next-line no-console
      console.warn('Voice outbound call:', error);
      return false;
    }
  }, [enabled, disableButtons, dispatch]);

  const cancelCall = useCallback(async () => {
    if (!enabled) return;
    const callId = callInfoDataRef.current?.callId
      ?? hangupStateRef.current.outboundDialingCallControlId
      ?? hangupStateRef.current.activeCallControlId;
    if (!callId) return;
    const endedReason = resolveEndedReasonForVoiceCall(hangupStateRef.current, callId)
      === VOICE_ENDED_REASON_HANGUP
      ? VOICE_ENDED_REASON_HANGUP
      : VOICE_ENDED_REASON_AGENT_CANCELLED;
    disableButtons();
    await hangupTrackedCall(callId, endedReason);
    callUiEvents?.clearCallReferences?.();
  }, [enabled, disableButtons, hangupTrackedCall, callUiEvents]);

  if (!enabled) {
    return {
      answerCall: noop,
      declineCall: noop,
      endCall: noop,
      toggleMic: noopBool,
      call: noop,
      cancelCall: noop
    };
  }

  return {
    answerCall,
    declineCall,
    endCall,
    toggleMic,
    call,
    cancelCall
  };
}
